'use client';
import React, { useState } from 'react';
import { Play, CheckCircle2, Factory, Zap, ShieldCheck } from 'lucide-react';
import JsonLd from '@/components/common/JsonLd';

/**
 * ProcessVideoSection - Showcases the manufacturing excellence at Royal Prime Labels.
 * Includes visual player, process details, and VideoObject schema.
 */
const ProcessVideoSection = () => {
    const [isPlaying, setIsPlaying] = useState(false);

    const videoData = {
        name: "Label Manufacturing Process at Royal Prime Labels",
        description: "Explore the advanced 8-color flexographic printing and label manufacturing workflow at our HSIIDC Rai facility. Precision, quality, and industrial-scale production.",
        thumbnailUrl: "https://royalprimelabels.com/images/advanced_label_infrastructure.png",
        uploadDate: "2024-05-01T08:00:00+05:30",
        duration: "PT2M45S",
        contentUrl: "https://www.youtube.com/watch?v=YOUR_VIDEO_ID",
        embedUrl: "https://www.youtube.com/embed/YOUR_VIDEO_ID",
        viewCount: 1500
    };

    return (
        <section className="w-full bg-[#050f19] py-24 md:py-32 overflow-hidden relative">
            <JsonLd type="VideoObject" data={videoData} />
            
            {/* Ambient Background Effects */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/5 rounded-full blur-[120px] -z-0"></div>
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#4fd1d3]/5 rounded-full blur-[120px] -z-0"></div>

            <div className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
                    
                    {/* Left side: Strategic Text Content */}
                    <div className="flex flex-col gap-10">
                        <div className="flex flex-col gap-6">
                            <div className="flex items-center gap-3">
                                <span className="w-10 h-px bg-[#4fd1d3]"></span>
                                <span className="text-[#4fd1d3] text-sm font-bold tracking-[0.3em] uppercase">
                                    Our Production Excellence
                                </span>
                            </div>
                            
                            <h2 className="text-3xl md:text-5xl font-bold text-white leading-[1.15]">
                                How does flexographic <br />
                                <span className="text-slate-400 font-serif italic">label printing work?</span>
                            </h2>
                            
                            <p className="text-slate-400 text-lg leading-relaxed max-w-xl">
                                Step inside our world-class HSIIDC Rai facility. We combine high-speed 8-color flexographic printing with real-time quality control to deliver the gold standard in industrial labeling.
                            </p>
                        </div>

                        {/* Capability Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                            {[
                                { text: "8-Color Precision Flexo", icon: Factory },
                                { text: "In-line UV Curing", icon: Zap },
                                { text: "Die-cutting & Stripping", icon: ShieldCheck },
                                { text: "100% Visual Inspection", icon: ShieldCheck },
                                { text: "Moisture Barrier Coating", icon: Zap },
                                { text: "High-Speed Roll Finishing", icon: Factory }
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center gap-3 group">
                                    <div className="p-2 rounded-lg bg-white/5 border border-white/10 group-hover:bg-[#4fd1d3]/10 group-hover:border-[#4fd1d3]/20 transition-all duration-300">
                                        <item.icon className="w-4 h-4 text-[#4fd1d3]" />
                                    </div>
                                    <span className="text-slate-300 text-sm font-medium tracking-wide">{item.text}</span>
                                </div>
                            ))}
                        </div>

                        {/* Contextual CTAs */}
                        <div className="flex flex-col sm:flex-row items-center gap-5 pt-6 border-t border-white/10">
                            <a 
                                href="/quote" 
                                className="w-full sm:w-auto px-10 py-5 bg-[#4fd1d3] text-[#050f19] font-bold rounded-full hover:bg-white hover:-translate-y-1 transition-all duration-300 text-center shadow-lg shadow-[#4fd1d3]/10"
                            >
                                Get a Technical Quote
                            </a>
                            <a 
                                href="/contact" 
                                className="w-full sm:w-auto px-10 py-5 border border-white/20 text-white font-bold rounded-full hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 text-center"
                            >
                                Consult an Expert
                            </a>
                        </div>
                    </div>

                    {/* Right side: Premium Video Player */}
                    <div className="relative group">
                        <div className="relative aspect-video rounded-[40px] overflow-hidden border border-white/10 bg-slate-950 shadow-2xl overflow-hidden">
                            {!isPlaying ? (
                                <>
                                    {/* Video Poster */}
                                    <img 
                                        src="/images/advanced_label_infrastructure.png" 
                                        alt="Label manufacturing facility production line" 
                                        className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-1000 ease-out"
                                    />
                                    
                                    {/* Play Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors duration-500">
                                        <button 
                                            onClick={() => setIsPlaying(true)}
                                            className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#4fd1d3] text-[#050f19] flex items-center justify-center shadow-[0_20px_50px_rgba(79,209,211,0.3)] hover:scale-110 active:scale-95 transition-all duration-300 relative z-10"
                                            aria-label="Play process walkthrough video"
                                        >
                                            <Play className="w-10 h-10 sm:w-12 sm:h-12 fill-current ml-1.5" />
                                        </button>
                                        
                                        {/* Pulses */}
                                        <div className="absolute w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#4fd1d3]/30 animate-ping"></div>
                                    </div>

                                    {/* Video Meta Info */}
                                    <div className="absolute bottom-10 left-10 right-10 text-white flex justify-between items-end">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                                                <span className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-70">Production Walkthrough</span>
                                            </div>
                                            <h3 className="text-xl sm:text-2xl font-bold">Inside Our HSIIDC Rai Facility</h3>
                                        </div>
                                        <span className="px-3 py-1 bg-black/40 backdrop-blur-md rounded-lg text-[10px] font-bold tracking-widest border border-white/10">02:45</span>
                                    </div>
                                </>
                            ) : (
                                <iframe 
                                    src="https://www.youtube.com/embed/YOUR_VIDEO_ID?autoplay=1" 
                                    title="Royal Prime Labels Manufacturing Process Walkthrough"
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                    allowFullScreen
                                ></iframe>
                            )}
                        </div>
                        
                        {/* Decorative floating labels/elements can be added here if needed */}
                        <div className="absolute -z-10 -top-10 -right-10 w-64 h-64 bg-[#4fd1d3]/10 rounded-full blur-[80px]"></div>
                        <div className="absolute -z-10 -bottom-10 -left-10 w-64 h-64 bg-blue-600/10 rounded-full blur-[80px]"></div>
                    </div>

                </div>

                {/* Additional Process Detail Row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-24 border-t border-white/5 pt-16">
                    <div className="flex flex-col gap-4">
                        <h4 className="text-white font-bold text-lg">Flexo Technology</h4>
                        <p className="text-slate-500 text-sm leading-relaxed">
                            Our 8-color presses utilize advanced flexographic technology to deliver vibrant, consistent colors across millions of labels with sub-millimeter registration.
                        </p>
                    </div>
                    <div className="flex flex-col gap-4">
                        <h4 className="text-white font-bold text-lg">Quality Assurance</h4>
                        <p className="text-slate-500 text-sm leading-relaxed">
                            Every roll undergoes rigorous 100% visual inspection and batch testing to ensure zero-defect delivery for critical Pharma and FMCG applications.
                        </p>
                    </div>
                    <div className="flex flex-col gap-4">
                        <h4 className="text-white font-bold text-lg">Scalable Capacity</h4>
                        <p className="text-slate-500 text-sm leading-relaxed">
                            Equipped with high-speed machinery, our facility is engineered to handle massive volume surges while maintaining the highest production standards.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProcessVideoSection;
